import React from "react";
import { Reveal, SectionLabel } from "./Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { Butterfly } from "./Brand";

export const WhatIsLivon = () => {
  const { t } = useLanguage();
  return (
    <section
      id="philosophy"
      data-testid="what-is-livon-section"
      className="relative py-28 md:py-40 bg-livon-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Top editorial: section label + butterfly mark + title */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel testId="what-label">{t.what.label}</SectionLabel>
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                className="serif text-livon-ink leading-[1.04] tracking-tight"
                style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", fontWeight: 500 }}
                data-testid="what-title"
              >
                {t.what.title}
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-5 flex lg:justify-end">
            <Reveal delay={0.1}>
              <div className="relative w-44 h-44 md:w-56 md:h-56 rounded-3xl bg-livon-surface border border-livon-line overflow-hidden">
                <div className="aurora" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-white border border-livon-line flex items-center justify-center shadow-[0_8px_30px_-10px_rgba(21,19,26,0.2)]">
                    <Butterfly size={64} />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Editorial philosophy text */}
        <div className="max-w-3xl mx-auto lg:mx-0 lg:ml-[8.333%]">
          <Reveal delay={0.05}>
            <p
              data-testid="what-intro"
              className="serif text-livon-ink leading-[1.4] mb-12 md:mb-16"
              style={{ fontSize: "clamp(1.35rem, 1.8vw, 1.75rem)", fontWeight: 400 }}
            >
              {t.what.intro}
            </p>
          </Reveal>

          <div className="space-y-6 md:space-y-7 text-livon-muted text-base md:text-lg leading-relaxed">
            {t.what.paragraphs.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.04}>
                <p data-testid={`what-p-${i}`}>{p}</p>
              </Reveal>
            ))}
          </div>

          {/* Pull quote */}
          <Reveal delay={0.18}>
            <figure
              className="my-14 md:my-20 border-l-2 border-livon-lavender pl-6 md:pl-10"
              data-testid="what-quote"
            >
              <blockquote
                className="serif italic text-livon-ink leading-[1.25]"
                style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.25rem)", fontWeight: 400 }}
              >
                “{t.what.quote}”
              </blockquote>
            </figure>
          </Reveal>

          {/* Paragraphs after quote */}
          <div className="space-y-6 md:space-y-7 text-livon-muted text-base md:text-lg leading-relaxed">
            {t.what.paragraphsAfter.map((p, i) => (
              <Reveal key={i} delay={0.22 + i * 0.04}>
                <p data-testid={`what-pa-${i}`}>{p}</p>
              </Reveal>
            ))}
          </div>

          {/* Audience list removed — kept content lean; Body.Mind.Soul carries structure now */}

          {/* Closing line */}
          <Reveal delay={0.32}>
            <p
              data-testid="what-closing"
              className="mt-14 md:mt-20 serif italic text-livon-ink leading-[1.15]"
              style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)", fontWeight: 400 }}
            >
              {t.what.closing}
            </p>
          </Reveal>
        </div>

        {/* Pillars removed — Metoden / Appen / Utbildningen each have dedicated sections below */}
      </div>
    </section>
  );
};
